import { useState, type ReactNode } from 'react';

// CollapsibleSection — visual container for the "Daily log" / "Settings"
// groups on the Today screen. Collapsed by default to keep the page short;
// the header shows the eyebrow + a one-line summary string the parent
// supplies. Toggle is per-session (not persisted) so each visit is
// consistent.
//
// Mirror of the Card primitive's eyebrow + body shape so it visually fits
// alongside the focal cards above.
export function CollapsibleSection({
  eyebrow,
  summary,
  defaultOpen = false,
  children,
}: {
  eyebrow: string;
  // Single-line preview shown next to the eyebrow when collapsed (e.g.
  // "Sleep 7.5h · Energy 8 · No alcohol"). Hidden when expanded.
  summary?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  // A hairline outline on the canvas, not a white card: these groups are
  // deliberately quieter than the focal cards they sit between.
  return (
    <section className="rounded-panel border border-line">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-baseline justify-between gap-2 w-full px-4 py-3 text-left rounded-panel transition-colors hover:bg-fg/[0.03]"
        aria-expanded={open}
      >
        <span className="label-eyebrow">
          {open ? '▾' : '▸'} {eyebrow}
        </span>
        {!open && summary && (
          <span className="font-mono text-[10px] tracking-[0.14em] text-fg-faint truncate ml-2">
            {summary}
          </span>
        )}
      </button>
      {open && <div className="px-4 pb-4 space-y-4">{children}</div>}
    </section>
  );
}
